import React, { useState } from 'react';
import { Plus, Trash2, Pencil, CheckCircle, Shield, User, Lock } from 'lucide-react';

export default function UserManagement({ db, setDb, saveToDB }) {
  const [users, setUsers] = useState(db.users || []);
  const [form, setForm] = useState({ id: null, username: '', password: '', role: 'User', rights: [] });
  const [isEditing, setIsEditing] = useState(false);

  // Added 'Sourcing' to modules list
  const modules = ['Admin', 'Style', 'Costing', 'Planning', 'PartDNA', 'Workstation', 'Quality', 'Material', 'VAlibrary', 'Sourcing', 'Logistics'];

  const handleSave = async () => {
    if (!form.username || !form.password) return alert("Username and Password are required");
    
    const entry = form.id ? form : { ...form, id: Date.now() };
    let updatedUsers;
    
    if (isEditing) {
      updatedUsers = users.map(u => u.id === form.id ? entry : u);
    } else {
      if (users.some(u => u.username === form.username)) return alert("Username already exists");
      updatedUsers = [...users, entry];
    }

    setUsers(updatedUsers);
    setDb(prev => ({ ...prev, users: updatedUsers }));
    
    // DB Connection
    if (saveToDB) {
        await saveToDB('user', entry);
    }

    resetForm();
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this user?")) {
      const updated = users.filter(u => u.id !== id);
      setUsers(updated);
      setDb(prev => ({ ...prev, users: updated }));
    }
  };

  const handleEdit = (user) => {
    setForm(user);
    setIsEditing(true);
  };

  const resetForm = () => {
    setForm({ id: null, username: '', password: '', role: 'User', rights: [] });
    setIsEditing(false);
  };

  const toggleRight = (mod) => {
    setForm(prev => {
      const rights = prev.rights.includes(mod) 
        ? prev.rights.filter(r => r !== mod)
        : [...prev.rights, mod];
      return { ...prev, rights };
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '15px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'10px' }}>
            <User size={18}/> Registered Users
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                <th style={{ padding: '10px', fontSize: '12px' }}>Username</th>
                <th style={{ padding: '10px', fontSize: '12px' }}>Role</th>
                <th style={{ padding: '10px', fontSize: '12px' }}>Modules</th>
                <th style={{ padding: '10px', fontSize: '12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px', fontWeight: 'bold' }}>{u.username}</td>
                  <td style={{ padding: '10px' }}>{u.role}</td>
                  <td style={{ padding: '10px', fontSize: '11px', color: '#64748b' }}>
                    {u.rights.length === modules.length ? 'ALL ACCESS' : u.rights.join(', ')}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <button onClick={() => handleEdit(u)} style={{ marginRight: '10px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#2563eb' }}><Pencil size={14} /></button>
                    {u.username !== 'admin' && (
                      <button onClick={() => handleDelete(u.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ width: '350px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', height: 'fit-content' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isEditing ? 'Edit User' : 'Create User'}
          </h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Username</label>
            <input value={form.username} onChange={e => setForm({...form, username: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} disabled={form.username === 'admin'} />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Password</label>
            <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Role</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
              <option>User</option><option>Admin</option><option>Manager</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Access Rights</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {modules.map(mod => (
                <label key={mod} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.rights.includes(mod)} onChange={() => toggleRight(mod)} />
                  {mod}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSave} style={{ flex: 1, padding: '10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>{isEditing ? 'Update' : 'Save'}</button>
            {isEditing && (
              <button onClick={resetForm} style={{ padding: '10px', background: '#cbd5e1', color: '#0f172a', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}